import { isSameDay, subDays } from 'date-fns'
import { List } from 'immutable'
import React, { ReactElement, useEffect, useState } from 'react'
import { usePrevious } from 'react-use'

import { addAlertSignature } from '../../actions/alerts'
import { TimeRegistrationBulk } from '../../api/time-registrations'
import paths from '../../constants/paths'
import Company from '../../model/company'
import CompanyUser from '../../model/companyUser'
import Department from '../../model/department'
import Employee from '../../model/employee'
import LeaveType from '../../model/leaveType'
import TimeRegistration from '../../model/timeRegistration'
import { TimeRegistrationCreationFields, TimeRegistrationMutableFields } from '../../model/timeRegistration'
import { DateFormat } from '../../model/types'
import { EmployeeReducer } from '../../reducers/employees'
import { TimeRegistrationReducer } from '../../reducers/timeRegistrations'
import { EmployeeRowEmployeeDetails, getEmployeeRowEmployeeDetails } from '../../utils/approve-tab-utils'
import { formatDate, formatDateInterval, getDate } from '../../utils/date-utils'
import { formatError } from '../../utils/error-utils'
import { formatLeaveSubType, formatLeaveTypeName } from '../../utils/format-utils'
import { hasDepartmentPermission, hasEmployeeDepartmentPermission } from '../../utils/permissions-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import ContextMenu from '../elements/ContextMenu'
import Icon from '../elements/icon'
import Modal from '../elements/modal'
import Table from '../elements/table'
import { TableChange } from '../elements/table/Table'
import Title from '../elements/Title'
import TitleMenu from '../elements/TitleMenu'
import LeaveEdit from '../employees-single/leave/LeaveEdit'
import DumbLink from '../widgets/DumbLink'
import { ApproveCardEmployeeName } from './ApproveTabElements'

type Props = {
  company: Company
  companyUser?: CompanyUser
  timeRegistrations: TimeRegistrationReducer
  employees: EmployeeReducer

  departments: List<Department>
  companyUsers: List<CompanyUser>
  leaveTypes: List<LeaveType>

  addAlert: addAlertSignature
  getRemuneration: (id: string) => void
  createTimeRegistration: (reg: TimeRegistrationCreationFields) => void
  createTimeRegistrationBulk: (bulk: TimeRegistrationBulk) => void
  updateTimeRegistration: (reg: TimeRegistrationMutableFields) => void
  approveTimeRegistrations: (ids: string[]) => void
}

export default function LeaveRegistrationsCard(props: Props): ReactElement | null {
  const [modalKey, setModalKey] = useState(1)
  const [showEdit, setShowEdit] = useState<string | boolean>(false)
  const [editing, setEditing] = useState<string[]>([])

  type Sort = {
    sortOn?: 'employee' | 'date' | 'days'
    sortOrder?: 'ascend' | 'descend'
  }
  const [sort, setSort] = useState<Sort>({})

  const { timeRegistrations, addAlert } = props
  const previousTimeRegistrations = usePrevious(timeRegistrations)

  const setEditVisibility = (showEdit: string | boolean) => {
    // Increment modalKey to create a new component
    setModalKey((prev) => prev + 1)
    setShowEdit(showEdit)
  }

  useEffect(() => {
    if (previousTimeRegistrations && previousTimeRegistrations.saving && !timeRegistrations.saving) {
      if (timeRegistrations.error) {
        addAlert('error', formatError(timeRegistrations.error))
      } else {
        setEditVisibility(false)
      }
      setEditing([]) //empty it
    }
  }, [previousTimeRegistrations, timeRegistrations, addAlert, setEditing])

  // Convert employees Map to List for compatibility
  const employeesMap = new Map<string, Employee>()
  props.employees.employees.forEach((employee) => {
    employeesMap.set(employee.id, employee)
  })

  const pendingLeaveRegistrations: TimeRegistration[] = timeRegistrations.timeRegistrations
    .filter(
      (timeReg) =>
        !timeReg.approved &&
        (timeReg.class === 'Leave' || timeReg.class === 'Flex') &&
        !editing.some((edit) => edit === timeReg.id) &&
        hasEmployeeDepartmentPermission(props.companyUser, employeesMap, timeReg.employeeID, 'ApproveObjects')
    )
    .toArray()

  const approveAll = (e: React.MouseEvent) => {
    e.preventDefault()
    const unapproved = pendingLeaveRegistrations.map((reg) => reg.id)
    props.approveTimeRegistrations(unapproved)
  }

  type LeaveRegistrationRow = EmployeeRowEmployeeDetails & {
    key: string
    id: string[]
    employeeID: string
    date: string
    dateSort: DateFormat
    days: string
    daysNo: number
    leaveType: string
    leaveSubType?: string
    original: TimeRegistration[]
    canEditObjects: boolean
  }

  const columns = [
    {
      title: t('approve_tab.leave_registrations.header.employee'),
      dataIndex: '',
      key: 'xEmployee',
      sorter: 'employee',
      render: (row: LeaveRegistrationRow) => {
        return <ApproveCardEmployeeName {...row} linkTo={'/' + paths.EMPLOYEES + '/' + row.employeeID + '/leave'} />
      },
    },
    { title: t('approve_tab.leave_registrations.header.date'), dataIndex: 'date', key: 'date', sorter: 'date' },
    {
      title: t('approve_tab.leave_registrations.header.leave_type'),
      dataIndex: '',
      key: 'xLeaveType',
      render: (row: LeaveRegistrationRow) => {
        if (!row.leaveSubType) {
          return row.leaveType
        }
        return (
          <>
            {row.leaveType}
            <br />
            <small>{row.leaveSubType}</small>
          </>
        )
      },
    },
    { title: t('approve_tab.leave_registrations.header.days'), dataIndex: 'days', key: 'days', sorter: 'days' },
    {
      title: '',
      dataIndex: '',
      key: 'xApprove',
      render: (row: LeaveRegistrationRow) => {
        return (
          <span className="approve-link" onClick={() => props.approveTimeRegistrations(row.id)}>
            {t('approve_tab.leave_registrations.actions.approve')}
          </span>
        )
      },
    },
    {
      title: '',
      dataIndex: '',
      key: 'xActions',
      className: 'ant-table-col-context',
      render: (row: LeaveRegistrationRow) => {
        if (!row.canEditObjects) {
          return null
        }

        return (
          <ContextMenu placeholder={<Icon type="paperWithPencil" />}>
            <DumbLink onClick={() => (window.location.href = '/' + paths.EMPLOYEES + '/' + row.employeeID + '/leave')}>
              <Icon type="paperWithPencil" /> {t('leave_registrations.table.actions.edit')}
            </DumbLink>
          </ContextMenu>
        )
      },
    },
  ]

  const pendingLeaveRegistrationRows: LeaveRegistrationRow[] = pendingLeaveRegistrations
    .map((timeReg) => {
      let leaveLine = ''
      let subLine
      const leaveType = props.leaveTypes.find((type) => type.id === timeReg.leaveTypeID)
      if (leaveType) {
        leaveLine = formatLeaveTypeName(leaveType.name)
        if (timeReg.leaveSubTypeID) {
          const subType = leaveType.leaveSubTypes.find((subType) => subType.id === timeReg.leaveSubTypeID)
          if (subType) {
            subLine = formatLeaveSubType(subType.name)
          }
        }
      }
      return {
        key: timeReg.id,
        id: [timeReg.id],
        employeeID: timeReg.employeeID,
        ...getEmployeeRowEmployeeDetails(employeesMap.get(timeReg.employeeID), props.departments, props.companyUsers),
        date: formatDate(timeReg.date),
        dateSort: timeReg.date,
        days: t('unit.day_count', { count: timeReg.days || 1 }),
        daysNo: timeReg.days || 1,
        leaveType: leaveLine,
        leaveSubType: subLine,
        original: [timeReg],
        canEditObjects: hasEmployeeDepartmentPermission(
          props.companyUser,
          employeesMap,
          timeReg.employeeID,
          'EditObjects'
        ),
      }
    })
    .sort((a, b) => {
      if (a.employeeName === b.employeeName) {
        return a.original[0].date.localeCompare(b.original[0].date)
      }
      return a.employeeName.localeCompare(b.employeeName)
    })
    .reduce((list: LeaveRegistrationRow[], reg: LeaveRegistrationRow) => {
      if (list.length === 0) {
        return [reg]
      }
      const lastIdx = list.length - 1
      const last = list[lastIdx]
      if (
        last.employeeName !== reg.employeeName ||
        last.leaveType !== reg.leaveType ||
        last.leaveSubType !== reg.leaveSubType
      ) {
        return [...list, reg]
      }
      const thisDate = reg.original[0].date
      if (!isSameDay(subDays(getDate(thisDate), 1), getDate(last.original[last.original.length - 1].date))) {
        return [...list, reg]
      }
      last.id.push(reg.id[0])
      last.original.push(reg.original[0])
      const firstDate = last.original[0].date
      last.date = formatDateInterval(getDate(firstDate), getDate(thisDate))
      last.dateSort = firstDate
      last.daysNo = last.original.reduce((days, reg) => days + (reg.days || 1), 0)
      last.days = t('unit.day_count', { count: last.daysNo })
      last.canEditObjects = last.canEditObjects && reg.canEditObjects // Ensure all grouped items have edit permission
      list[lastIdx] = last
      return list
    }, [])
    .sort((a, b) => {
      let i = a
      let j = b
      if (sort.sortOrder === 'descend') {
        j = a
        i = b
      }
      switch (sort.sortOn) {
        case 'employee':
          if (i.employeeName === j.employeeName) {
            return a.dateSort.localeCompare(b.dateSort)
          }
          return i.employeeName.localeCompare(j.employeeName)
        case 'date':
          if (i.dateSort === j.dateSort) {
            return a.employeeName.localeCompare(b.employeeName)
          }
          return i.dateSort.localeCompare(j.dateSort)
        case 'days':
          if (i.daysNo === j.daysNo) {
            if (i.employeeName === j.employeeName) {
              return a.dateSort.localeCompare(b.dateSort)
            }
            return a.employeeName.localeCompare(b.employeeName)
          }
          return j.daysNo - i.daysNo
        default:
          if (a.employeeName === b.employeeName) {
            return a.dateSort.localeCompare(b.dateSort)
          }
          return a.employeeName.localeCompare(b.employeeName)
      }
    })

  const tableChange = ({ sorter }: TableChange<LeaveRegistrationRow>) => {
    if (!sorter.column) {
      return
    }
    switch (sorter.column.sorter) {
      case 'employee':
      case 'date':
      case 'days':
        setSort({ sortOn: sorter.column.sorter, sortOrder: sorter.order })
        break
      default:
        break
    }
  }

  if (pendingLeaveRegistrations.length === 0) {
    return null
  }

  const employee = props.employees.employees.find((employee) => employee.id === showEdit)

  return (
    <div className="approve-box">
      <TitleMenu>
        <Button onClick={approveAll} type="primary" size="extra-extra-large">
          {t('approve_tab.leave_registrations.actions.approve_all')}
        </Button>
      </TitleMenu>
      <Title>{t('approve_tab.leave_registrations.title')}</Title>
      <Table columns={columns} dataSource={pendingLeaveRegistrationRows} onChange={tableChange} pagination={false} />

      <Modal
        key={modalKey}
        visible={!!showEdit}
        onOk={() => setEditVisibility(false)}
        onCancel={() => setEditVisibility(false)}
        width={582}
        footer={null}
      >
        <LeaveEdit
          visible={!!showEdit}
          company={props.company}
          employee={employee}
          employees={props.employees}
          canApproveObjects={hasDepartmentPermission(
            props.companyUser,
            employee && employee.departmentID,
            'ApproveObjects'
          )}
          timeRegistrations={props.timeRegistrations}
          leaveTypes={props.leaveTypes}
          remunerationType={employee && employee.activeContract && employee.activeContract.remunerationType}
          getRemuneration={props.getRemuneration}
          createTimeRegistration={props.createTimeRegistration}
          createTimeRegistrationBulk={props.createTimeRegistrationBulk}
          updateTimeRegistration={props.updateTimeRegistration}
        />
      </Modal>
    </div>
  )
}
